import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ZoomIn } from "lucide-react";

const CROP_ASPECT = 4 / 3;

/** Crops `imageSrc` (object URL) down to the given pixel area and returns a new File. */
const getCroppedFile = (imageSrc, area, fileName) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = area.width;
      canvas.height = area.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
      canvas.toBlob(
        (blob) => (blob ? resolve(new File([blob], fileName, { type: "image/jpeg" })) : reject(new Error("Crop failed"))),
        "image/jpeg",
        0.92
      );
    };
    img.onerror = reject;
    img.src = imageSrc;
  });

/** Reusable 4:3 crop dialog — used for every image upload across the admin panel. */
export const ImageCropModal = ({ open, imageSrc, fileName, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleCropComplete = useCallback((_, areaPixels) => setCroppedAreaPixels(areaPixels), []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const file = await getCroppedFile(imageSrc, croppedAreaPixels, fileName || "photo.jpg");
      onConfirm(file);
    } finally {
      setSaving(false);
    }
  };

  if (!imageSrc) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="max-w-2xl bg-[var(--bg-secondary)] border-[var(--border-default)] text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Crop Photo (4:3)</DialogTitle>
        </DialogHeader>
        <p className="text-white/60 text-sm">
          Drag to reposition, use the slider to zoom. Every product photo is cropped to 4:3 for a consistent look across cards, galleries and hero images.
        </p>
        <div className="relative w-full h-[380px] bg-black" data-testid="image-crop-area">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={CROP_ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>
        <div className="flex items-center gap-3 mt-2">
          <ZoomIn size={16} className="text-white/60 shrink-0" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.02}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            data-testid="image-crop-zoom-slider"
            className="flex-1"
          />
        </div>
        <DialogFooter>
          <button type="button" onClick={onCancel} data-testid="image-crop-cancel" className="btn-secondary">
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={saving} data-testid="image-crop-confirm" className="btn-primary">
            {saving ? "Processing…" : "Save Cropped Photo"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
